import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/base/ArticleCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import { articles, categories } from '@/mocks/articles';
import { siteInfo } from '@/mocks/siteInfo';

function HeroSection() {
  const featuredArticle = articles.find((a) => a.isHot && a.isNew) || articles[0];

  return (
    <section className="relative w-full min-h-[580px] md:min-h-[650px] flex items-end pb-16 md:pb-20 overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=A%20dramatic%20dark%20entertainment%20backdrop%20with%20glowing%20spotlights%20and%20stage%20smoke%2C%20luxurious%20red%20velvet%20curtains%2C%20paparazzi%20flash%20bulbs%2C%20mysterious%20cinematic%20atmosphere%2C%20Hollywood%20premiere%20vibe%2C%20dark%20elegant%20colors%20with%20warm%20accent%20highlights&width=1600&height=900&seq=chigua-hero-bg&orientation=landscape"
        alt="吃瓜黑料"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/70"></div>

      <div className="relative z-10 chigua-container text-center">
        <h1 className="text-3xl md:text-5xl font-heading font-bold text-white mb-4 leading-tight">
          {siteInfo.tagline}
        </h1>
        <p className="text-sm md:text-base text-white/80 max-w-xl mx-auto mb-8 leading-relaxed">
          {siteInfo.description}
        </p>
        <div className="flex items-center justify-center gap-3 flex-wrap">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className="px-5 py-2.5 text-sm rounded-full bg-white/15 text-white border border-white/25 hover:bg-white/25 hover:border-white/40 transition-all cursor-pointer whitespace-nowrap backdrop-blur-sm no-underline"
            >
              {cat.name}
            </Link>
          ))}
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10">
        <div className="w-5 h-8 border-2 border-white/30 rounded-full flex items-start justify-center">
          <div className="w-1 h-2 bg-white/60 rounded-full mt-1.5 animate-bounce"></div>
        </div>
      </div>
    </section>
  );
}

function HotSection() {
  const hotArticles = articles.filter((a) => a.isHot);

  return (
    <section className="py-12 md:py-16">
      <div className="chigua-container">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-1 h-6 bg-primary-500 rounded-full"></div>
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
              热搜爆款
            </h2>
          </div>
          <Link
            to="/category/mingxing-heiliao"
            className="text-sm text-foreground-500 hover:text-primary-600 transition-colors flex items-center gap-1 no-underline"
          >
            查看全部 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {hotArticles.slice(0, 6).map((article, index) => (
            <ArticleCard
              key={article.id}
              article={article}
              variant={index === 0 ? 'featured' : 'default'}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function CategorySection() {
  const [activeCategory, setActiveCategory] = useState(categories[0].slug);
  const filteredArticles = useMemo(
    () => articles.filter((a) => a.category === activeCategory),
    [activeCategory]
  );

  return (
    <section className="py-12 md:py-16 bg-background-100/60">
      <div className="chigua-container">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-1 h-6 bg-accent-500 rounded-full"></div>
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
            分类吃瓜
          </h2>
        </div>

        <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
          {categories.map((cat) => (
            <button
              key={cat.slug}
              onClick={() => setActiveCategory(cat.slug)}
              className={`px-5 py-2.5 text-sm rounded-full transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                activeCategory === cat.slug
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-50 text-foreground-600 hover:bg-background-200 border border-background-200'
              }`}
            >
              <i className={`${cat.icon} text-base`}></i>
              {cat.name}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {filteredArticles.slice(0, 4).map((article) => (
            <ArticleCard key={article.id} article={article} />
          ))}
        </div>
      </div>
    </section>
  );
}

function LatestSection() {
  const latestArticles = [...articles].sort(
    (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
  );

  return (
    <section className="py-12 md:py-16">
      <div className="chigua-container">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-1 h-6 bg-secondary-500 rounded-full"></div>
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
            最新爆料
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {latestArticles.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>

          <div className="lg:col-span-1">
            <div className="bg-background-100/60 rounded-lg p-5 sticky top-20">
              <h3 className="text-base font-heading font-bold text-foreground-900 mb-4 flex items-center gap-2">
                <i className="ri-fire-line text-accent-500"></i>
                热门排行
              </h3>
              <div className="flex flex-col gap-3">
                {[...articles]
                  .sort((a, b) => b.views - a.views)
                  .slice(0, 8)
                  .map((article, index) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.id}`}
                      className="flex items-start gap-3 group no-underline cursor-pointer"
                    >
                      <span
                        className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                          index < 3
                            ? 'bg-primary-500 text-background-50'
                            : 'bg-background-200 text-foreground-500'
                        }`}
                      >
                        {index + 1}
                      </span>
                      <span className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
                        {article.title}
                      </span>
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="py-14 md:py-20 relative overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=A%20dramatic%20dark%20stage%20with%20red%20velvet%20curtains%20and%20warm%20spotlight%20glow%2C%20entertainment%20industry%20aesthetic%2C%20elegant%20theater%20atmosphere%2C%20cinematic%20lighting%20with%20rich%20warm%20tones&width=1600&height=600&seq=chigua-cta-bg&orientation=landscape"
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-background-950/75"></div>
      <div className="relative z-10 chigua-container text-center">
        <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-4">
          有猛料？来爆料！
        </h2>
        <p className="text-sm text-background-300 max-w-lg mx-auto mb-8 leading-relaxed">
          如果你是圈内人士，掌握一手爆料信息，欢迎联系我们。严格保密爆料人信息，重大爆料更有丰厚奖励。
        </p>
        <Link
          to="/about"
          className="inline-flex items-center gap-2 px-7 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap no-underline"
        >
          <i className="ri-mail-send-line"></i>
          联系我们
        </Link>
      </div>
    </section>
  );
}

export default function Home() {
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteInfo.name,
    url: 'https://chiguaheila.com',
    description: siteInfo.description,
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://chiguaheila.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${siteInfo.name} - 首页`,
    description: siteInfo.description,
    url: 'https://chiguaheila.com',
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: 'https://chiguaheila.com',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(webpageSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <HeroSection />
          <HotSection />
          <CategorySection />
          <LatestSection />
          <CTASection />
        </main>
        <Footer />
      </div>
    </>
  );
}